import type { MatrixPos } from "@app/core";

export interface BoardLayoutSource {
  id: string;
  title: string;
  pos: MatrixPos;
  /** Optional rendered text following the title, e.g. the stale-age badge. */
  trailingText?: string;
  /** Number of fixed-size trailing icons rendered after the title. */
  trailingIconCount?: number;
}

export interface BoardLayoutNode {
  id: string;
  itemIds: string[];
  pos: MatrixPos;
  x: number;
  y: number;
  width: number;
  /** Collapsed height. The renderer sizes an EXPANDED block from
   *  `expandedClusterHeight` instead, so this stays the packing box. */
  height: number;
  visibleCount: number;
  /** Rows the packer budgeted away. The renderer derives its own count from the
   *  items it actually resolved, so treat this as the model's intent. */
  overflowCount: number;
  clustered: boolean;
  displaced: boolean;
}

export interface BoardLayout {
  nodes: BoardLayoutNode[];
  /** Diagnostics: which pass produced this layout and whether it still has
   *  overlap. Not rendered — they exist so the packing rules stay testable. */
  compactClusters: boolean;
  unresolvedCollisions: boolean;
}

/** Rendering metrics the pure packer cannot read off the DOM. */
export interface BoardLayoutMetrics {
  /** `--app-text-scale`; the root font size scales every rem-based chip size. */
  textScale?: number;
  /** Touch input — rows grow to a finger-sized height. */
  coarsePointer?: boolean;
}

interface Group {
  sources: BoardLayoutSource[];
  pos: MatrixPos;
}

interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const SIMILAR_DISTANCE = 0.058;
// Measured off the rendered chips at textScale 1 (a single pill lays out at
// 26px, a cluster row at 23px, a 3-row block at 76px), then rounded UP by ~1px.
// These MUST track the chip classes in MatrixBoard: too-short boxes let the
// packer certify a layout that visibly overlaps, which is the whole point of it.
const SINGLE_HEIGHT = 27;
const SINGLE_HEIGHT_COARSE = 31;
const CLUSTER_ROW_HEIGHT = 23;
const CLUSTER_ROW_HEIGHT_COARSE = 31;
const CLUSTER_SHELL_HEIGHT = 7;
const BOARD_MARGIN = 4;
const COLLISION_GAP = 5;
const OFFSET_STEP = 9;
const MAX_DISPLACEMENT = 92;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const scaled = (value: number, textScale: number) => value * textScale;

interface Metrics {
  textScale: number;
  rowHeight: number;
  singleHeight: number;
}

function resolveMetrics({ textScale = 1, coarsePointer = false }: BoardLayoutMetrics): Metrics {
  return {
    textScale: clamp(textScale, 0.75, 1.5),
    rowHeight: coarsePointer ? CLUSTER_ROW_HEIGHT_COARSE : CLUSTER_ROW_HEIGHT,
    singleHeight: coarsePointer ? SINGLE_HEIGHT_COARSE : SINGLE_HEIGHT,
  };
}

/** Height an EXPANDED block wants: every row plus its sticky collapse footer. */
export function expandedClusterHeight(count: number, metrics: BoardLayoutMetrics = {}): number {
  const { rowHeight, textScale } = resolveMetrics(metrics);
  return scaled((count + 1) * rowHeight + CLUSTER_SHELL_HEIGHT, textScale);
}

function distance(a: MatrixPos, b: MatrixPos): number {
  return Math.hypot(a.urgency - b.urgency, a.importance - b.importance);
}

function centroid(sources: BoardLayoutSource[]): MatrixPos {
  return {
    urgency: sources.reduce((sum, source) => sum + source.pos.urgency, 0) / sources.length,
    importance: sources.reduce((sum, source) => sum + source.pos.importance, 0) / sources.length,
  };
}

/**
 * Greedy nearest-centroid grouping with a strict radius. The extra all-members
 * check prevents a chain of individually-near points from creating one block
 * whose endpoints are no longer meaningfully "the same position".
 */
export function groupNearbyBoardItems(sources: BoardLayoutSource[]): Group[] {
  const groups: Group[] = [];
  for (const source of sources) {
    let best: Group | null = null;
    let bestDistance = Infinity;
    for (const group of groups) {
      const d = distance(source.pos, group.pos);
      if (
        d <= SIMILAR_DISTANCE &&
        d < bestDistance &&
        group.sources.every((member) => distance(source.pos, member.pos) <= SIMILAR_DISTANCE * 1.35)
      ) {
        best = group;
        bestDistance = d;
      }
    }
    if (!best) {
      groups.push({ sources: [source], pos: source.pos });
      continue;
    }
    best.sources.push(source);
    best.pos = centroid(best.sources);
  }
  return groups;
}

function textWidth(source: BoardLayoutSource, clustered: boolean, textScale: number): number {
  const perCharacter = clustered ? 5.5 : 6.1;
  const chrome = clustered ? 42 : 38;
  const trailingTextWidth = source.trailingText
    ? 6 + Math.min(source.trailingText.length, 10) * (clustered ? 5 : 5.4)
    : 0;
  const trailingIconWidth = (source.trailingIconCount ?? 0) * 16;
  return scaled(
    chrome + Math.min(source.title.length, 24) * perCharacter + trailingTextWidth + trailingIconWidth,
    textScale,
  );
}

function dimensions(group: Group, boardWidth: number, rowCap: number, metrics: Metrics): {
  width: number;
  height: number;
  visibleCount: number;
  overflowCount: number;
} {
  const { textScale } = metrics;
  const clustered = group.sources.length > 1;
  if (!clustered) {
    const widthFraction = (boardWidth < 280 ? 0.34 : 0.58) * textScale;
    const maxWidth = Math.min(scaled(176, textScale), boardWidth * Math.min(widthFraction, 0.76));
    return {
      width: clamp(
        textWidth(group.sources[0]!, false, textScale),
        Math.min(scaled(74, textScale), maxWidth),
        maxWidth,
      ),
      height: scaled(metrics.singleHeight, textScale),
      visibleCount: 1,
      overflowCount: 0,
    };
  }
  const visibleCount = Math.min(rowCap, group.sources.length);
  const overflowCount = group.sources.length - visibleCount;
  // Track the widest WIDTH, not the widest source: comparing sources re-measured
  // both sides on every step, so a cluster cost 2n `textWidth` calls per pass.
  let widestWidth = textWidth(group.sources[0]!, true, textScale);
  for (let i = 1; i < group.sources.length; i++) {
    const width = textWidth(group.sources[i]!, true, textScale);
    if (width > widestWidth) widestWidth = width;
  }
  const widthFraction = (rowCap === 2 ? 0.46 : 0.58) * textScale;
  const maxWidth = Math.min(scaled(176, textScale), boardWidth * Math.min(widthFraction, 0.76));
  return {
    width: clamp(
      widestWidth,
      Math.min(scaled(126, textScale), maxWidth),
      maxWidth,
    ),
    height: scaled(visibleCount * metrics.rowHeight + CLUSTER_SHELL_HEIGHT, textScale),
    visibleCount,
    overflowCount,
  };
}

function boxAt(x: number, y: number, width: number, height: number): Box {
  return {
    left: x - width / 2,
    top: y - height / 2,
    right: x + width / 2,
    bottom: y + height / 2,
  };
}

function overlaps(a: Box, b: Box): boolean {
  return !(
    a.right + COLLISION_GAP <= b.left ||
    a.left >= b.right + COLLISION_GAP ||
    a.bottom + COLLISION_GAP <= b.top ||
    a.top >= b.bottom + COLLISION_GAP
  );
}

function overlapArea(a: Box, b: Box): number {
  const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return width * height;
}

/** Grid cell for {@link createBoxIndex}. Chips are at most ~176×76, so a cell
 *  this size keeps a probe to a handful of buckets while staying coarse enough
 *  that a box lands in only a few of them. */
const INDEX_CELL = 64;

function forEachCell(box: Box, margin: number, visit: (key: number) => void): void {
  const x0 = Math.floor((box.left - margin) / INDEX_CELL);
  const x1 = Math.floor((box.right + margin) / INDEX_CELL);
  const y0 = Math.floor((box.top - margin) / INDEX_CELL);
  const y1 = Math.floor((box.bottom + margin) / INDEX_CELL);
  for (let cx = x0; cx <= x1; cx++) {
    for (let cy = y0; cy <= y1; cy++) visit(cx * 1_000_003 + cy);
  }
}

/**
 * Uniform-grid index over the boxes already placed. The packer probes ~100
 * spiral spots per group, and scanning EVERY placed box per probe made a
 * crowded board quadratic in group count — the dominant cost of a drop once a
 * board holds a few hundred chips. Boxes are small relative to the board, so
 * bucketing keeps each probe local. A stamp per box de-duplicates the boxes
 * that a probe reaches through more than one cell, which the overlap SUM
 * (unlike a plain hit test) would otherwise double-count.
 */
function createBoxIndex() {
  const boxes: Box[] = [];
  const cells = new Map<number, number[]>();
  const stamps: number[] = [];
  let probe = 0;
  return {
    add(box: Box): void {
      const index = boxes.length;
      boxes.push(box);
      stamps.push(0);
      forEachCell(box, 0, (key) => {
        const bucket = cells.get(key);
        if (bucket) bucket.push(index);
        else cells.set(key, [index]);
      });
    },
    /** Total overlap area with the placed boxes, and whether any of them is
     *  within {@link COLLISION_GAP} — the same verdict a linear scan gave. The
     *  stamp de-duplicates boxes this probe reaches through more than one cell,
     *  which the SUM (unlike the hit test) would otherwise double-count. Cells
     *  are walked inline rather than through a callback: this runs ~100 times per
     *  chip and the closure allocation showed up on a full board. */
    probe(box: Box): { overlap: number; collides: boolean } {
      const stamp = ++probe;
      let overlap = 0;
      let collides = false;
      const x0 = Math.floor((box.left - COLLISION_GAP) / INDEX_CELL);
      const x1 = Math.floor((box.right + COLLISION_GAP) / INDEX_CELL);
      const y0 = Math.floor((box.top - COLLISION_GAP) / INDEX_CELL);
      const y1 = Math.floor((box.bottom + COLLISION_GAP) / INDEX_CELL);
      for (let cx = x0; cx <= x1; cx++) {
        for (let cy = y0; cy <= y1; cy++) {
          const bucket = cells.get(cx * 1_000_003 + cy);
          if (!bucket) continue;
          for (const i of bucket) {
            if (stamps[i] === stamp) continue;
            stamps[i] = stamp;
            const other = boxes[i]!;
            if (!overlaps(box, other)) continue;
            collides = true;
            overlap += overlapArea(box, other);
          }
        }
      }
      return { overlap, collides };
    },
  };
}

/** Golden-angle spiral of candidate spots, densest first, out to the cap. */
function candidateOffsets(): Array<{ dx: number; dy: number }> {
  const offsets = [{ dx: 0, dy: 0 }];
  for (let n = 1; ; n++) {
    const radius = OFFSET_STEP * Math.sqrt(n);
    if (radius > MAX_DISPLACEMENT) break;
    const angle = n * 2.399963;
    offsets.push({ dx: Math.cos(angle) * radius, dy: Math.sin(angle) * radius });
  }
  return offsets;
}

const OFFSETS = candidateOffsets();

function placeGroups(
  groups: Group[],
  boardWidth: number,
  boardHeight: number,
  rowCap: number,
  metrics: Metrics,
): BoardLayout {
  const nodes: BoardLayoutNode[] = [];
  const index = createBoxIndex();
  let unresolvedCollisions = false;

  // Reused across every probe: the spiral scan allocated a Box per candidate,
  // which on a full board meant tens of thousands of short-lived objects.
  const scratch: Box = { left: 0, top: 0, right: 0, bottom: 0 };

  for (const group of groups) {
    const size = dimensions(group, boardWidth, rowCap, metrics);
    const anchorX = group.pos.urgency * boardWidth;
    const anchorY = (1 - group.pos.importance) * boardHeight;
    const minX = BOARD_MARGIN + size.width / 2;
    const maxX = boardWidth - BOARD_MARGIN - size.width / 2;
    const minY = BOARD_MARGIN + size.height / 2;
    const maxY = boardHeight - BOARD_MARGIN - size.height / 2;
    const probeAt = (offset: { dx: number; dy: number }) => {
      const x = clamp(anchorX + offset.dx, minX, maxX);
      const y = clamp(anchorY + offset.dy, minY, maxY);
      scratch.left = x - size.width / 2;
      scratch.right = x + size.width / 2;
      scratch.top = y - size.height / 2;
      scratch.bottom = y + size.height / 2;
      return { x, y };
    };

    // The spiral's radius grows monotonically, so the first spot that does not
    // collide is also the least-displaced one — take it and stop. Otherwise keep
    // the least-bad spot: smallest total overlap area, ties broken by the smaller
    // displacement. Distances are compared SQUARED (no `Math.hypot` per probe)
    // and the candidate box is a reused scratch object, so a full spiral scan
    // allocates nothing.
    let placed: { x: number; y: number; collides: boolean; movedSq: number } | null = null;
    let bestOverlap = Infinity;
    for (const offset of OFFSETS) {
      const { x, y } = probeAt(offset);
      const { overlap, collides } = index.probe(scratch);
      const dx = x - anchorX;
      const dy = y - anchorY;
      const movedSq = dx * dx + dy * dy;
      if (!collides) {
        placed = { x, y, collides: false, movedSq };
        break;
      }
      if (!placed || overlap < bestOverlap || (overlap === bestOverlap && movedSq < placed.movedSq)) {
        bestOverlap = overlap;
        placed = { x, y, collides: true, movedSq };
      }
    }
    if (!placed) continue;
    if (placed.collides) unresolvedCollisions = true;
    index.add(boxAt(placed.x, placed.y, size.width, size.height));
    const ids = group.sources.map((source) => source.id);
    nodes.push({
      id: ids.length === 1 ? ids[0]! : `cluster:${[...ids].sort().join(":")}`,
      itemIds: ids,
      pos: group.pos,
      x: placed.x,
      y: placed.y,
      width: size.width,
      height: size.height,
      visibleCount: size.visibleCount,
      overflowCount: size.overflowCount,
      clustered: ids.length > 1,
      displaced: placed.movedSq > 1,
    });
  }

  return { nodes, compactClusters: rowCap === 2, unresolvedCollisions };
}

/**
 * Adaptive board layout:
 *  1. isolated items stay at their exact position;
 *  2. only near-identical positions become multi-row blocks;
 *  3. every node is moved by the smallest collision-free spiral offset;
 *  4. if three-row blocks cannot fit, blocks collapse to two rows + overflow.
 */
export function buildAdaptiveBoardLayout(
  sources: BoardLayoutSource[],
  boardWidth: number,
  boardHeight: number,
  metrics: BoardLayoutMetrics = {},
): BoardLayout {
  if (boardWidth <= 0 || boardHeight <= 0) {
    return { nodes: [], compactClusters: false, unresolvedCollisions: false };
  }
  const groups = groupNearbyBoardItems(sources).sort((a, b) => {
    const aCluster = a.sources.length > 1;
    const bCluster = b.sources.length > 1;
    if (aCluster !== bCluster) return aCluster ? -1 : 1;
    if (aCluster && bCluster) {
      return b.pos.importance - a.pos.importance || a.pos.urgency - b.pos.urgency;
    }
    return a.pos.urgency - b.pos.urgency || b.pos.importance - a.pos.importance;
  });
  const resolved = resolveMetrics(metrics);
  const roomy = placeGroups(groups, boardWidth, boardHeight, 3, resolved);
  if (!roomy.unresolvedCollisions || !groups.some((group) => group.sources.length > 2)) return roomy;
  return placeGroups(groups, boardWidth, boardHeight, 2, resolved);
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

function sameNode(a: BoardLayoutNode, b: BoardLayoutNode): boolean {
  return (
    a.id === b.id && a.x === b.x && a.y === b.y &&
    a.width === b.width && a.height === b.height &&
    a.visibleCount === b.visibleCount && a.overflowCount === b.overflowCount &&
    a.clustered === b.clustered && a.displaced === b.displaced &&
    a.pos.urgency === b.pos.urgency && a.pos.importance === b.pos.importance &&
    sameIds(a.itemIds, b.itemIds)
  );
}

/**
 * Carry object IDENTITY over from the previous layout for every node the repack
 * left structurally unchanged (and reuse the whole layout when none moved).
 *
 * The packer is pure and rebuilds every node, so a one-chip drop handed the
 * renderer a fresh object per node — which re-rendered a board's worth of
 * `motion` wrappers even though only one of them had actually moved, the bulk of
 * the drop's frame cost. Diffing here keeps the packer pure and lets the
 * renderer memoize on node identity.
 */
export function reconcileBoardLayout(previous: BoardLayout | null, next: BoardLayout): BoardLayout {
  if (!previous) return next;
  const previousById = new Map(previous.nodes.map((node) => [node.id, node]));
  const nodes = next.nodes.map((node) => {
    const old = previousById.get(node.id);
    return old && sameNode(old, node) ? old : node;
  });
  const unchanged = nodes.length === previous.nodes.length
    && nodes.every((node, i) => node === previous.nodes[i]);
  return unchanged
    && previous.compactClusters === next.compactClusters
    && previous.unresolvedCollisions === next.unresolvedCollisions
    ? previous
    : { ...next, nodes };
}
